import Component from '@bythepixel/component-loader';
import { eventThrottle } from '@Core/utility';

export default class CollapsibleText extends Component {
  static selector = '[data-comp-collapsible-text]';
  static loaderPriority = 'in-view';

  constructor(container) {
    super(container);
    this.collapseText = this.$container.dataset.collapseText;
    this.collapsibleTextContent = this.$container.querySelector(
      '[data-comp-collapsible-text-content]',
    );
    this.collapsibleTextContentHeight = this.$container.querySelector(
      '[data-comp-collapsible-text-content-height]',
    );
    this.triggerButton = this.$container.querySelector(
      '[data-comp-collapsible-text-toggle]',
    );

    if (!this.triggerButton) {
      return;
    }

    this.triggerButtonText = this.triggerButton.querySelector('.btn__text') ?? this.triggerButton;

    this.init();
  }

  init() {
    const currentHeight = this.collapsibleTextContent.offsetHeight;
    const computedHeight = parseInt(
      getComputedStyle(this.collapsibleTextContentHeight).maxHeight,
    );

    if (currentHeight > computedHeight) {
      this.collapseContent();
      this.$container.dataset.collapsed = 'true';
      this.showTriggerButton();
      this.initEventListeners();
    } else {
      this.hideTriggerButton();
    }
    this.observeInternalLabelsForVisibility();
  }

  collapseContent() {
    this.collapsibleTextContent.classList.add('collapsed');
  }

  hideTriggerButton() {
    if (this.triggerButton) this.triggerButton.style.display = 'none';
  }

  showTriggerButton() {
    if (this.triggerButton) this.triggerButton.style.display = 'block';
  }

  initEventListeners() {
    this.triggerButton.addEventListener('click', () => {
      this.expandOrCollapse();
    });
  }

  expandOrCollapse() {
    if (this.$container.dataset.collapsed === 'false') {
      this.collapseTextContainer();
    } else if (this.$container.dataset.collapsed === 'true') {
      this.expandTextContainer();
    }
  }

  expandTextContainer() {
    this.$container.dataset.collapsed = 'false';
    this.collapsibleTextContent.classList.add('max-lines__expanded');

    if (this.triggerButton) this.triggerButtonText.innerHTML = this.collapseText;
  }

  collapseTextContainer() {
    this.$container.dataset.collapsed = 'true';
    this.collapsibleTextContent.classList.remove('max-lines__expanded');

    if (this.triggerButton) this.triggerButtonText.innerHTML = this.$container.dataset.expandText;
  }

  /**
   * Observes the internal labels of the collapsible text content for visibility changes
   * Only applies when this component is used with filters inside the collapsible text content
   * This is to update the trigger button text with the number of hidden labels when the user filters the content
   * as long as the original text had numbers in it.
   */
  observeInternalLabelsForVisibility() {
    const containsLabels = this.collapsibleTextContent.querySelectorAll('label');
    if (!containsLabels.length) return;

    const numberOfAlwaysVisibleLabels = Number(
      this.collapsibleTextContent.parentElement.dataset.alwaysVisibleAmount,
    ) || 0;

    const callback = () => {
      const visibleLabels = this.collapsibleTextContent.querySelectorAll(
        'label:not([style*="display: none"])',
      ).length;
      if (visibleLabels <= numberOfAlwaysVisibleLabels) {
        this.hideTriggerButton();
        return;
      }
      this.showTriggerButton();

      if (this.triggerButtonText.textContent.match(/\d+/g)) {
        const hiddenLabels = visibleLabels - numberOfAlwaysVisibleLabels;
        this.triggerButtonText.textContent = this.triggerButtonText.textContent.replace(/\d+/g, hiddenLabels);
      }
    };

    const observer = new MutationObserver(eventThrottle(callback, 50));
    const config = { attributes: true, childList: true, subtree: true };
    observer.observe(this.collapsibleTextContent, config);
  }
}
